import {
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import CountrySelectMenu from '../../../../Select/CountrySelectMenu';
import {
  AudienceSet,
  Brand,
  BulkSearchBody,
  CampaignAdSet,
  ChangeEventType,
  Country,
  DemographicsSet,
  FbTargetSearch,
  GeolocationLocationSet,
  GeolocationLocationSetData,
  IGeolocationSet,
} from '../../../../../types';
import { FloatingButton, PrimaryButton } from '../../../../Buttons';
import {
  fbBulkSearch,
  getFbTargetingReachEstimate,
} from '../../../../../services/ads';
import { buildAddress } from '../../../../../utils/helpers/DiyHelpers';
import { numberShortener } from '../../../../../utils/numberFormatter';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';
import GeolocationList from './GeolocationList';
import { FBOptimizationGoal } from '../../../../../utils/helpers/facebookAdsHelper';
import Spinner from '../../../../Spinner';
import FacebookMap from './FacebookMap';
import PopupModal from '../../../../Modal';
import SearchAddressForm from './SearchAddressForm';
import { constantStringToHumanized } from '../../../../../utils/stringModifier';
import {
  createOrUpdateLocationSet,
  getBrandAudienceSets,
  getBrandDemographicsSets,
  getSingleBrandLocationSets,
} from '../../../../../services/assets';
import { useForm } from 'react-hook-form';
import {
  assetsSectionStyling,
  demographicsToText,
  getLocationFieldName,
  parsedDetailedTargeting,
  targetsToText,
} from '../../../../../utils/helpers/AssetHelpers';
import { BusinessType } from '../../../../../utils/helpers/BrandHelpers';
import { HtmlTooltip } from '../../../../Tooltip';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import PageHeader from '../../../../PageHeader';
import ModalHeader from '../../../../Modal/ModalHeader';

const LOCATION_TYPES: any[] = [
  { label: 'All', value: 'all' },
  { label: 'Address', value: 'address' },
  { label: 'Neighborhood', value: 'neighborhood' },
  { label: 'Subcity/Borough', value: 'subcity' },
  { label: 'City', value: 'city' },
  //{ label: 'Residential Area', value: 'small_geo_area' },
  { label: 'County', value: 'medium_geo_area' },
  //{ label: 'District', value: 'large_geo_area' },
  { label: 'Zip Code', value: 'zip' },
  { label: 'DMA', value: 'geo_market' },
  { label: 'Electoral District', value: 'electoral_district' },
  { label: 'State/Province', value: 'region' },
  { label: 'Country', value: 'country' },
];

interface GeolocationSetFormProps {
  brand: Brand;
  onClose: () => void;
  edit?: boolean;
  locationSetId?: string | null;
  isCreateAds?: boolean;
  // If used in Create Campaign Form
  geolocationSets?: IGeolocationSet[];
  setGeolocationSets?: Dispatch<SetStateAction<IGeolocationSet[]>>;
  setFilteredGeolocationSets?: Dispatch<SetStateAction<IGeolocationSet[]>>;
  onSelectGeolocationSet?: (geolocationSet: IGeolocationSet) => void;
}

const GeolocationSetForm: React.FC<GeolocationSetFormProps> = ({
  brand,
  onClose,
  edit = false,
  locationSetId = null,
  isCreateAds = false,
  // If used in Create Campaign Form
  geolocationSets = [],
  setGeolocationSets = null,
  setFilteredGeolocationSets = null,
  onSelectGeolocationSet = null,
}) => {
  const {
    formState: { errors },
    register,
    handleSubmit,
    clearErrors,
  } = useForm({ mode: 'onSubmit' });
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));

  const [formValues, setFormValues] = useState<IGeolocationSet>({
    name: '',
    description: '',
    primary: true,
    geolocations: {
      countries: [],
      cities: [],
      zips: [],
      regions: [],
      geo_markets: [],
      custom_locations: [],
    },
    excludedGeolocations: {
      countries: [],
      cities: [],
      zips: [],
      regions: [],
      geo_markets: [],
      custom_locations: [],
    },
    minReach: 0,
    maxReach: 0,
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [geolocations, setGeolocations] = useState<
    GeolocationLocationSetData[]
  >([]);
  const [filteredGeolocations, setFilteredGeolocations] = useState<
    GeolocationLocationSetData[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [map, setMap] = useState<any>(null);
  const [reachEstimate, setReachEstimate] = useState({
    users_lower_bound: 0,
    users_upper_bound: 0,
  });
  const [reachError, setReachError] = useState<string>(null);
  const [reachLoading, setReachLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [searchFilters, setSearchFilters] = useState<string[]>(['all']);
  const [limit, setLimit] = useState<number>(25);
  const [openAddressSearch, setOpenAddressSearch] = useState<boolean>(false);
  const [isEditCustomLocations, setIsEditCustomLocations] =
    useState<boolean>(false);
  const [customLocationsIndex, setCustomLocationsIndex] = useState<number>(0);
  const [editExcludedCustomLocations, setEditExcludedCustomLocations] =
    useState<boolean>(false);
  const [countryFilters, setCountryFilters] = useState<Country[]>([
    {
      code: 'US',
      label: 'United States',
      phone: '1',
    },
  ]);
  const [defaultAudienceSet, setDefaultAudienceSet] =
    useState<AudienceSet>(null);
  const [defaultDemographicsSet, setDefaultDemographicsSet] =
    useState<DemographicsSet>(null);
  const [confirmEdit, setConfirmEdit] = useState<boolean>(false);
  const [selectAllLoading, setSelectAllLoading] = useState<boolean>(false);
  const [defaultCityRadius, setDefaultCityRadius] = useState<number>(50);

  useEffect(() => {
    if (brand) {
      getPrimaryAudienceSet();
      getPrimaryDemographicsSet();
    }
  }, [brand]);

  useEffect(() => {
    if (edit && locationSetId) {
      getLocationSet();
    }
  }, [edit, locationSetId, brand]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const targets = parsedDetailedTargeting(defaultAudienceSet?.targets);

      let temp: any = {
        ...targets,
        geo_locations: formValues?.geolocations,
        excluded_geo_locations: formValues?.excludedGeolocations,
        genders: defaultDemographicsSet?.genders || [1, 2],
        age_min:
          defaultDemographicsSet?.age_min ||
          brand?.businessType === BusinessType.FRANDEV
            ? 25
            : 18,
        age_max:
          defaultDemographicsSet?.age_max ||
          brand?.businessType === BusinessType.FRANDEV
            ? 59
            : 64,
      };

      getFacebookTargetReach(temp);
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [formValues?.geolocations, formValues?.excludedGeolocations]);

  const getCoordinates = async (address: string) => {
    const request = {
      query: address,
      fields: ['ALL'],
    };

    let service = new google.maps.places.PlacesService(map);

    return new Promise((resolve, reject) => {
      service.findPlaceFromQuery(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const coordinates = {
            latitude: results[0].geometry.location.lat(),
            longitude: results[0].geometry.location.lng(),
            placeId: results[0].place_id,
          };
          resolve(coordinates);
        } else {
          reject(new Error(`Place query failed with status: ${status}`));
        }
      });
    });
  };

  const getLocationSet = async () => {
    try {
      const response = await getSingleBrandLocationSets(
        brand?._id,
        locationSetId,
      );

      setFormValues(response.data);
    } catch (error: any) {
      console.log(error);
    }
  };

  const getFacebookTargetReach = async (targeting: FbTargetSearch) => {
    try {
      setReachLoading(true);

      const response = await getFbTargetingReachEstimate({
        brandId: brand?._id,
        targets: targeting,
        optimizationGoal: FBOptimizationGoal.LeadGeneration,
      });

      setReachEstimate(response.data);
      setReachError(null);
    } catch (error: any) {
      setReachEstimate({
        users_lower_bound: 0,
        users_upper_bound: 0,
      });
      const err = error.response.data;
      setReachError(err?.message);
    } finally {
      setReachLoading(false);
    }
  };

  const getPrimaryAudienceSet = async () => {
    try {
      const response = await getBrandAudienceSets({
        brandId: brand._id,
        primary: true,
      });

      setDefaultAudienceSet(response.data[0]);
    } catch (error: any) {
      console.log(error);
    }
  };

  const getPrimaryDemographicsSet = async () => {
    try {
      const response = await getBrandDemographicsSets({
        brandId: brand._id,
        primary: true,
      });

      setDefaultDemographicsSet(response.data[0]);
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleOnSubmit = async () => {
    try {
      if (reachError) {
        return dispatch(
          toggleAlert({
            toggle: true,
            message: reachError,
            type: 'error',
          }),
        );
      }

      if (isEmptyGeolocations(formValues?.geolocations)) {
        return dispatch(
          toggleAlert({
            toggle: true,
            message: 'Please include at least one location',
            type: 'error',
          }),
        );
      }

      if (formValues?.usageCount > 0 && !confirmEdit && edit) {
        return setConfirmEdit(true);
      }

      setLoading(true);

      let temp: IGeolocationSet = {
        ...formValues,
        minReach: reachEstimate.users_lower_bound,
        maxReach: reachEstimate.users_upper_bound,
      };

      const { usageCount, adsUsage, ...params } = temp;

      const response = await createOrUpdateLocationSet({
        brandId: brand?._id,
        body: params,
        locationSetId: edit ? locationSetId : null,
      });

      const data: IGeolocationSet = response.data;

      if (isCreateAds) {
        if (edit) {
          let temp: IGeolocationSet[] = [];
          geolocationSets.forEach((geolocationSet: IGeolocationSet) => {
            if (geolocationSet._id === data._id) {
              geolocationSet = data;
            }

            temp = [...temp, geolocationSet];
          });

          setGeolocationSets(temp);
          setFilteredGeolocationSets(temp);
        } else {
          setGeolocationSets((prev) => [data, ...prev]);
          setFilteredGeolocationSets((prev) => [data, ...prev]);
        }

        onSelectGeolocationSet(data);
      }

      dispatch(
        toggleAlert({
          toggle: true,
          message: 'Your location set has been saved successfully',
        }),
      );

      onClose();
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleOnSelectCountries = (e: ChangeEventType, value: Country[]) => {
    setCountryFilters(value);
  };

  const handleOnSearch = async () => {
    try {
      if (!searchQuery.replace(/\s/g, '').length) {
        return dispatch(
          toggleAlert({
            toggle: true,
            message: 'Please enter a search query',
            type: 'error',
          }),
        );
      }

      setSearchLoading(true);

      const countryCodes = countryFilters.map(
        (country: Country) => country.code,
      );
      let params: BulkSearchBody = {
        type: 'adgeolocation',
        searchQueries: searchQuery.split('\n').filter((value) => value),
        locationTypes: searchFilters.includes('all') ? [] : searchFilters,
        countryCodes,
      };

      const response = await fbBulkSearch(brand?._id, params);

      setGeolocations(response.data);
      setFilteredGeolocations(response.data);
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setSearchLoading(false);
    }
  };

  const handleOnChangeQueries = (e: ChangeEventType) => {
    setSearchQuery(e.target.value);
  };

  const getCurrentGeolocation = (
    formValues: IGeolocationSet,
    geolocation: GeolocationLocationSetData,
    exclude: boolean,
  ) => {
    let temp: any[] = [];

    const data = () => {
      if (exclude) {
        return formValues?.excludedGeolocations;
      }

      return formValues?.geolocations;
    };

    switch (geolocation.type) {
      case 'city':
      case 'neighborhood':
      case 'subcity':
      case 'small_geo_area':
      case 'medium_geo_area':
      case 'large_geo_area':
        temp = [...(data()?.cities || [])];
        break;
      case 'zip':
        temp = [...(data()?.zips || [])];
        break;
      case 'region':
        temp = [...(data()?.regions || [])];
        break;
      case 'geo_market':
        temp = [...(data()?.geo_markets || [])];
        break;
      case 'electoral_district':
        temp = [...(data()?.electoral_districts || [])];
        break;
      default:
        temp = [...(data()?.countries || [])];
        break;
    }

    return temp;
  };

  const handleSelectLocation = async (
    value: GeolocationLocationSetData,
    exclude: boolean,
  ) => {
    getCoordinates(buildAddress(value, true))
      .then((coordinates: any) => {
        let tempFormValues: IGeolocationSet = { ...formValues };

        let tempGeolocations: any[] = getCurrentGeolocation(
          tempFormValues,
          value,
          exclude,
        );

        let params: any = { ...value, ...coordinates };

        if (value.type === 'city') {
          params = {
            ...params,
            radius: defaultCityRadius,
            distance_unit: 'mile',
          };
        }

        tempGeolocations = [...tempGeolocations, params];

        if (exclude) {
          tempFormValues = {
            ...tempFormValues,
            excludedGeolocations: {
              ...tempFormValues.excludedGeolocations,
              [getLocationFieldName(value.type)]: tempGeolocations,
            },
          };
        } else {
          tempFormValues = {
            ...tempFormValues,
            geolocations: {
              ...tempFormValues.geolocations,
              [getLocationFieldName(value.type)]: tempGeolocations,
            },
          };
        }

        setFormValues(tempFormValues);
      })
      .catch((error: any) => {
        console.log(error);
      });
  };

  const handleSelectAllLocation = async (exclude = false) => {
    try {
      setSelectAllLoading(true);

      let temp: IGeolocationSet = { ...formValues };

      for (const geolocation of filteredGeolocations) {
        const excluded = formValues?.excludedGeolocations[
          getLocationFieldName(geolocation.type)
        ]?.filter(
          (includedLocation: GeolocationLocationSetData) =>
            includedLocation.key === geolocation.key,
        );

        const included = formValues?.geolocations[
          getLocationFieldName(geolocation.type)
        ]?.filter(
          (includedLocation: GeolocationLocationSetData) =>
            includedLocation.key === geolocation.key,
        );

        if (!excluded.length && !included.length) {
          let coordinates: any;
          try {
            coordinates = await getCoordinates(buildAddress(geolocation, true));
          } catch (error: any) {
            console.log(error.message);
          }

          let tempGeolocations: any[] = getCurrentGeolocation(
            temp,
            geolocation,
            exclude,
          );

          let params: any = { ...geolocation, ...((coordinates as any) || {}) };

          if (geolocation.type === 'city') {
            params = {
              ...params,
              radius: defaultCityRadius,
              distance_unit: 'mile',
            };
          }

          tempGeolocations = [...tempGeolocations, params];

          if (exclude) {
            temp = {
              ...temp,
              excludedGeolocations: {
                ...temp.excludedGeolocations,
                [getLocationFieldName(geolocation.type)]: tempGeolocations,
              },
            };
          } else {
            temp = {
              ...temp,
              geolocations: {
                ...temp.geolocations,
                [getLocationFieldName(geolocation.type)]: tempGeolocations,
              },
            };
          }
        }
      }

      setFormValues(temp);
    } catch (error: any) {
      console.log(error);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleOnSearchLocation = (e: ChangeEventType) => {
    setSearch(e.target.value);
    let temp: GeolocationLocationSetData[] = [];

    if (e.target.value === '') {
      temp = geolocations;
    } else {
      temp = geolocations.filter((location: GeolocationLocationSetData) =>
        location.name.toLowerCase().includes(e.target.value.toLowerCase()),
      );
    }

    setFilteredGeolocations(temp);
  };

  const handleOnChangeRadius = (
    radius: string,
    value: GeolocationLocationSetData,
  ) => {
    const field = getLocationFieldName(value.type);
    const isCustomLocations = field === 'custom_locations';

    const temp = formValues?.geolocations[field].map(
      (location: GeolocationLocationSetData) => {
        if (
          (!isCustomLocations && location.key === value.key) ||
          (isCustomLocations &&
            location.latitude === value.latitude &&
            location.longitude === value.longitude)
        ) {
          return {
            ...location,
            radius: parseInt(radius),
          };
        }

        return location;
      },
    );

    setFormValues({
      ...formValues,
      geolocations: {
        ...formValues.geolocations,
        [field]: temp,
      },
    });
  };

  const handleOnRemoveLocation = (
    value: GeolocationLocationSetData,
    exclude = false,
  ) => {
    const field = getLocationFieldName(value.type);
    const isCustomLocation = field === 'custom_locations';

    const data = () => {
      if (exclude) {
        return formValues?.excludedGeolocations;
      }

      return formValues?.geolocations;
    };

    const temp = data()[field].filter(
      (location: GeolocationLocationSetData) =>
        (isCustomLocation && location.key !== value.key) ||
        (!isCustomLocation &&
          location.latitude !== value.latitude &&
          location.longitude !== value.longitude),
    );

    if (exclude) {
      setFormValues({
        ...formValues,
        excludedGeolocations: {
          ...formValues.excludedGeolocations,
          [field]: temp,
        },
      });
    } else {
      setFormValues({
        ...formValues,
        geolocations: {
          ...formValues.geolocations,
          [field]: temp,
        },
      });
    }
  };

  const handleOnChange = (e: ChangeEventType) => {
    if (e.target.name === 'name') {
      clearErrors('name');
    }

    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenSearchAddress = (
    isEdit = false,
    index = 0,
    exclude = false,
  ) => {
    setOpenAddressSearch(true);

    if (isEdit) {
      setIsEditCustomLocations(true);
      setCustomLocationsIndex(index);
      setEditExcludedCustomLocations(exclude);
    }
  };

  const handleCloseSearchAddress = () => {
    setOpenAddressSearch(false);
    setIsEditCustomLocations(false);
    setCustomLocationsIndex(0);
    setEditExcludedCustomLocations(false);
  };

  const isEmptyGeolocations = (geolocations: GeolocationLocationSet) => {
    return Object.values(geolocations).every(
      (field) => Array.isArray(field) && field.length === 0,
    );
  };

  const humanizeLocationTitle = (type: string) => {
    if (type === 'geo_markets') return 'DMAs';

    if (type === 'custom_locations') return 'Addresses';

    return constantStringToHumanized(type);
  };

  const handleCloseConfirmEdit = () => {
    setConfirmEdit((prev) => !prev);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {isCreateAds ? (
          <Grid item xs={12}>
            <PageHeader
              title={`${edit ? 'EDIT' : 'CREATE'} GEOLOCATION SET`}
              textAlign="left"
              marginBottom="0"
            />
          </Grid>
        ) : null}

        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            ...(!isMobile ? assetsSectionStyling(isCreateAds) : {}),
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                {...register('name', {
                  required: true,
                })}
                fullWidth
                required
                variant="standard"
                type="text"
                id="name"
                name="name"
                label="Location Set Name"
                onChange={handleOnChange}
                value={formValues?.name}
                InputLabelProps={{ shrink: true }}
                size="small"
                autoFocus
                error={!!errors?.name}
                helperText={errors?.name && 'Location set name is required'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="standard"
                type="text"
                id="description"
                name="description"
                label="Description"
                onChange={handleOnChange}
                value={formValues?.description}
                InputLabelProps={{ shrink: true }}
                size="small"
                multiline
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={formValues?.primary} />
                }
                label="Set as default location set"
                value={formValues?.primary}
                onChange={(e: any) => {
                  setFormValues({
                    ...formValues,
                    primary: e.target.checked,
                  });
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1">
                Filter target geolocation based on a country
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <CountrySelectMenu
                value={countryFilters}
                onChange={handleOnSelectCountries}
                shrink
                multiple
              />
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        value={searchQuery}
                        name="body"
                        onChange={handleOnChangeQueries}
                        InputLabelProps={{ shrink: true }}
                        multiline
                        label="Enter Locations"
                        minRows={4}
                        maxRows={100}
                        helperText={`Suggested Format: ${
                          searchFilters.includes('address')
                            ? 'Street Address, City, State, Country'
                            : 'City, State'
                        }`}
                        placeholder={
                          searchFilters.includes('address')
                            ? `Enter your address; eg. \n8 Christopher St, New York, New York, United States`
                            : `Enter locations separated by a new line; eg. \nNew York, New York\nLos Angeles, California`
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      {LOCATION_TYPES.map(
                        (locationType: { value: string; label: string }) => {
                          const selected = searchFilters.includes(
                            locationType.value,
                          );

                          return (
                            <FormControlLabel
                              key={`location-type-${locationType.value}`}
                              control={
                                <Checkbox size="small" checked={selected} />
                              }
                              label={locationType.label}
                              value={selected}
                              onChange={(e: any) => {
                                let temp: string[] = [];
                                if (e.target.checked) {
                                  if (locationType.value === 'all') {
                                    temp = ['all'];
                                  } else if (locationType.value === 'address') {
                                    temp = ['address'];
                                  } else {
                                    temp = searchFilters.filter(
                                      (filter: string) =>
                                        filter !== 'all' &&
                                        filter !== 'address',
                                    );

                                    temp = [...temp, locationType.value];
                                  }
                                } else {
                                  temp = searchFilters.filter(
                                    (filter: string) =>
                                      filter !== locationType.value,
                                  );
                                }

                                setSearchFilters(temp);
                              }}
                            />
                          );
                        },
                      )}
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sx={{ justifyContent: 'flex-end', display: 'flex' }}
                    >
                      <PrimaryButton
                        title="Search Location"
                        type="button"
                        handleOnClick={() =>
                          searchFilters.includes('address')
                            ? handleOpenSearchAddress()
                            : handleOnSearch()
                        }
                        loading={searchLoading}
                      />
                    </Grid>

                    {searchLoading ? (
                      <Grid
                        item
                        xs={12}
                        sx={{ justifyContent: 'center', display: 'flex' }}
                      >
                        <CircularProgress />
                      </Grid>
                    ) : geolocations.length > 0 ? (
                      <>
                        <Grid item xs={12}>
                          <Divider>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 'bold', textAlign: 'center' }}
                            >
                              {filteredGeolocations.length} Results Found
                            </Typography>
                          </Divider>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            type="text"
                            id="search"
                            name="search"
                            label="Search"
                            onChange={handleOnSearchLocation}
                            value={search}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>
                        {geolocations
                          ?.map(
                            (location: GeolocationLocationSetData) =>
                              location.type,
                          )
                          ?.includes('city') ? (
                          <Grid
                            item
                            xs={12}
                            sx={{
                              justifyContent: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              component="div"
                              variant="caption"
                            >{`Default City Radius: ${
                              defaultCityRadius || 50
                            } miles`}</Typography>

                            <Slider
                              component="div"
                              defaultValue={50}
                              min={1}
                              max={50}
                              valueLabelDisplay="auto"
                              value={defaultCityRadius}
                              onChange={(e: any) =>
                                setDefaultCityRadius(parseInt(e.target.value))
                              }
                              sx={{ width: '300px' }}
                            />
                          </Grid>
                        ) : null}

                        <Grid
                          item
                          xs={12}
                          sx={{ justifyContent: 'flex-end', display: 'flex' }}
                        >
                          <PrimaryButton
                            title="Include All"
                            type="button"
                            variant="text"
                            handleOnClick={() => handleSelectAllLocation()}
                            loading={selectAllLoading}
                          />

                          <PrimaryButton
                            title="Exclude All"
                            type="button"
                            variant="text"
                            handleOnClick={() => handleSelectAllLocation(true)}
                            loading={selectAllLoading}
                          />
                        </Grid>

                        {filteredGeolocations
                          ?.slice(0, limit)
                          ?.map((geolocation: GeolocationLocationSetData) => {
                            return (
                              <Grid item xs={12} key={geolocation.key}>
                                <GeolocationList
                                  geolocation={geolocation}
                                  search
                                  formValues={formValues}
                                  onSelectLocation={handleSelectLocation}
                                  withAction
                                />
                              </Grid>
                            );
                          })}

                        {limit < filteredGeolocations?.length ? (
                          <Grid
                            item
                            xs={12}
                            sx={{ justifyContent: 'center', display: 'flex' }}
                          >
                            <PrimaryButton
                              title="Load More"
                              type="button"
                              variant="outlined"
                              handleOnClick={() =>
                                setLimit((prev) => prev + 25)
                              }
                              rounded
                            />
                          </Grid>
                        ) : null}
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', textAlign: 'center' }}
                        >
                          No Results Found
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/** Map Section */}
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            ...(!isMobile ? assetsSectionStyling(isCreateAds) : {}),
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{ flexDirection: 'column', display: 'flex' }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Estimated Audience Size:{' '}
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ marginLeft: '10px' }}
                >
                  {reachLoading ? (
                    <Spinner />
                  ) : (
                    `${numberShortener(
                      reachEstimate.users_lower_bound,
                    )} - ${numberShortener(reachEstimate.users_upper_bound)}`
                  )}
                </Typography>
              </Typography>

              <Typography
                variant="caption"
                sx={{ alignItems: 'center', display: 'flex' }}
              >
                <HtmlTooltip
                  title={`Audiences\n${targetsToText(
                    defaultAudienceSet?.targets,
                  )}\nDemographics\n${demographicsToText(
                    defaultDemographicsSet,
                    brand,
                  )}`}
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  <InfoTwoToneIcon
                    sx={{
                      fontSize: '18px',
                      color: '#096F4D',
                    }}
                  />
                </HtmlTooltip>

                <Box sx={{ marginLeft: '5px' }}>
                  Computed based on default audience and demographics sets
                </Box>
              </Typography>

              {reachError ? (
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 'bold', color: 'red' }}
                >
                  {reachError}
                </Typography>
              ) : null}
            </Grid>

            <Grid item xs={12}>
              <FacebookMap
                geolocations={formValues?.geolocations}
                excludedGeolocations={formValues?.excludedGeolocations}
                setParentMap={setMap}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  marginRight: '10px',
                  textAlign: 'center',
                }}
              >
                Included
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    {!isEmptyGeolocations(formValues?.geolocations) ? (
                      Object.keys(formValues?.geolocations).map(
                        (key: string) => {
                          if (formValues?.geolocations[key]?.length === 0) {
                            return <></>;
                          }

                          return (
                            <>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  {humanizeLocationTitle(key)}
                                </Typography>
                              </Grid>

                              {formValues?.geolocations[key].map(
                                (geolocation: GeolocationLocationSetData) => {
                                  return (
                                    <Grid item xs={12}>
                                      <GeolocationList
                                        geolocation={geolocation}
                                        formValues={formValues}
                                        onRemove={handleOnRemoveLocation}
                                        onChangeRadius={handleOnChangeRadius}
                                        withAction
                                      />
                                    </Grid>
                                  );
                                },
                              )}
                            </>
                          );
                        },
                      )
                    ) : (
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', textAlign: 'center' }}
                        >
                          No Locations Added
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  marginRight: '10px',
                  textAlign: 'center',
                }}
              >
                Excluded
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    {!isEmptyGeolocations(formValues?.excludedGeolocations) ? (
                      Object.keys(formValues?.excludedGeolocations).map(
                        (key: string) => {
                          if (
                            formValues?.excludedGeolocations[key]?.length === 0
                          )
                            return <></>;

                          return (
                            <>
                              <Grid item xs={12}>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  {humanizeLocationTitle(key)}
                                </Typography>
                              </Grid>

                              {formValues?.excludedGeolocations[key].map(
                                (geolocation: GeolocationLocationSetData) => {
                                  return (
                                    <Grid item xs={12}>
                                      <GeolocationList
                                        exclude
                                        geolocation={geolocation}
                                        formValues={formValues}
                                        onRemove={handleOnRemoveLocation}
                                        onChangeRadius={handleOnChangeRadius}
                                        withAction
                                      />
                                    </Grid>
                                  );
                                },
                              )}
                            </>
                          );
                        },
                      )
                    ) : (
                      <Grid item xs={12}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', textAlign: 'center' }}
                        >
                          No Locations Added
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ paddingTop: '20px' }}>
        <FloatingButton
          title="Save"
          onClick={handleSubmit(handleOnSubmit)}
          loading={loading}
          bottom="60px"
          right="180px"
          fixed
        />

        <FloatingButton
          title="Cancel"
          onClick={onClose}
          bottom="60px"
          right="85px"
          fixed
          theme="red"
        />
      </Box>

      <PopupModal
        open={openAddressSearch}
        handleClose={handleCloseSearchAddress}
      >
        <SearchAddressForm
          formValues={formValues}
          setFormValues={setFormValues}
          address={searchQuery}
          isEdit={isEditCustomLocations}
          index={customLocationsIndex}
          exclude={editExcludedCustomLocations}
          onClose={handleCloseSearchAddress}
        />
      </PopupModal>

      <PopupModal open={confirmEdit} handleClose={handleCloseConfirmEdit}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <ModalHeader title="Confirm Edit Template" />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{ textAlign: 'center', fontWeight: 'bold' }}
            >
              Modifying this template will affect other ad sets that use it.
              Confirm edit?
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', textAlign: 'center' }}
            >
              {formValues?.adsUsage
                ?.map((adset: CampaignAdSet) => `${adset.name} (${adset.id})`)
                ?.join('\n')}
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          sx={{ position: 'sticky', bottom: 0 }}
          mt={2}
        >
          <Grid item xs={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <PrimaryButton
              title="Cancel"
              type="button"
              handleOnClick={handleCloseConfirmEdit}
              loading={loading}
              marginRight5
            />

            <PrimaryButton
              title="Confirm"
              type="button"
              handleOnClick={handleOnSubmit}
              loading={loading}
            />
          </Grid>
        </Grid>
      </PopupModal>
    </Box>
  );
};

export default GeolocationSetForm;
